import React, { useEffect, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import PageHeader from '../../shared/page-header/PageHeader';
import SKAlerts, { CreateAlert } from '../../shared/common-alerts/SKAlerts';
import TodoListGrid from './TodoListGrid';
import TodoListModal from './TodoListModal';
import { getApiBaseUrl } from '../../utils/apiConfig';
import { getStorageValues } from '../../shared/CommonFunctions';
import { getModulePermissions } from '../../utils/permissionHelper';
import { logAuditEvent } from '../../utils/auditLogger';

function TodoListMain(props) {
  const [todoList, setTodoList] = useState([]);
  const [modalIsOpen, setIsOpen] = useState(false);
  const [actionType, setActionType] = useState('Add');
  const [selectedRow, setSelectedRow] = useState(null);
  const [alert, setAlert] = useState(null);
  const [userModulePermission, setUserModulePermission] = useState({});
  const moduleName = props.Module || 'Todo List';

  useEffect(() => {
    const modulePermissions = getModulePermissions(props.Module);
    setUserModulePermission(modulePermissions ? modulePermissions : {});
    fetchTodoList();
  }, [props.Module]);

  useEffect(() => {
    logAuditEvent(moduleName, 'Accessed Module', `Accessed ${moduleName}`);
  }, [moduleName]);

  const fetchTodoList = async () => {
    try {
      const response = await axios.get(getApiBaseUrl() + '/todolist/getAllV2', {
        params: {
          UserId: getStorageValues('userId')
        }
      });

      if (response.status === 200 && response.data?.success) {
        const rows = Array.isArray(response.data?.data?.[0])
          ? response.data.data[0]
          : (Array.isArray(response.data?.data) ? response.data.data : []);
        setTodoList(rows);
      } else {
        setTodoList([]);
      }
    } catch (error) {
      console.log(error);
      CreateAlert('Unable to fetch todo list', 'Error', setAlert);
    }
  };

  const openActionType = (row, action) => {
    setSelectedRow(row || null);
    setActionType(action);
    setIsOpen(true);
  };

  const openAddModal = () => {
    logAuditEvent(moduleName, 'Add Button Click', `Opened add ${moduleName} modal`);
    openActionType(null, 'Add');
  };

  const closeModal = () => {
    setIsOpen(false);
    setSelectedRow(null);
  };

  const validation = (todoData) => {
    if (!todoData?.Title || !todoData.Title.trim()) {
      CreateAlert('Please enter todo title!', 'Warning', setAlert);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e, data) => {
    const eventAction = e?.target?.name || actionType;

    if (eventAction === 'Delete') {
      try {
        const response = await axios.post(getApiBaseUrl() + '/todolist/delete', {
          TodoId: selectedRow?.TodoId,
          UserId: getStorageValues('userId')
        });

        if (response.status === 200 && response.data?.success) {
          CreateAlert(response.data.message || 'Todo deleted successfully', 'Success', setAlert);
          fetchTodoList();
          closeModal();
        } else {
          CreateAlert(response.data?.message || 'Unable to delete todo', 'Error', setAlert);
        }
      } catch (error) {
        console.log(error);
        CreateAlert(error?.response?.data?.message || 'Unable to delete todo', 'Error', setAlert);
      }
      return;
    }

    if (eventAction === 'Add' || eventAction === 'Edit') {
      const payload = {
        TodoId: eventAction === 'Edit' ? data?.TodoId : null,
        Title: data?.Title ? data.Title.trim() : '',
        Description: data?.Description || null,
        DueDate: data?.DueDate ? moment(data.DueDate).format('YYYY-MM-DD HH:mm:ss') : null,
        UserId: getStorageValues('userId')
      };

      if (!validation(payload)) {
        return;
      }

      try {
        const response = await axios.post(getApiBaseUrl() + '/todolist/save', payload);
        if (response.status === 200 && response.data?.success) {
          CreateAlert(response.data.message || 'Todo saved successfully', 'Success', setAlert);
          fetchTodoList();
          closeModal();
        } else {
          CreateAlert(response.data?.message || 'Unable to save todo', 'Error', setAlert);
        }
      } catch (error) {
        console.log(error);
        CreateAlert(error?.response?.data?.message || 'Unable to save todo', 'Error', setAlert);
      }
    }
  };

  return (
    <>
      <SKAlerts alert={alert} />
      <PageHeader
        displayModule={props.Module}
        onModelOpen={openAddModal}
        onRefresh={fetchTodoList}
        showPDF={false}
        showExcel={false}
        showPrint={false}
        showRefresh={true}
        showAddButton={userModulePermission.AddModule === 1}
      />

      <TodoListGrid
        TodoListData={todoList}
        UserPermissions={userModulePermission}
        onAction={openActionType}
        moduleName={moduleName}
      />

      <TodoListModal
        show={modalIsOpen}
        UserAction={actionType}
        onClose={closeModal}
        selectedRow={selectedRow}
        onSubmit={handleSubmit}
        moduleName={moduleName}
      />
    </>
  );
}

export default TodoListMain;
